"use client";

import { useState, useEffect, useCallback } from "react";
import { useUser } from "@clerk/nextjs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import * as Sentry from "@sentry/nextjs";


interface CustomWaitlistProps {
  waitlistId: string; // 예: "25-3-super-compressed"
}

export default function CustomWaitlist({ waitlistId }: CustomWaitlistProps) {
  const { user, isSignedIn } = useUser();
  const [email, setEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [isCheckingRegistration, setIsCheckingRegistration] = useState(true);
  const [error, setError] = useState("");

  // 등록 상태 확인
  const checkRegistrationStatus = useCallback(async () => {
    try {
      const requestBody: { waitlistId: string; email?: string; clerkUserId?: string } = {
        waitlistId,
      };

      // 로그인된 사용자인 경우 Clerk ID와 이메일 모두 전송
      if (isSignedIn && user) {
        requestBody.clerkUserId = user.id;
        requestBody.email = user.emailAddresses?.[0]?.emailAddress;
      }

      const response = await fetch("/api/waitlist/check-registration", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();

      if (response.ok && result.isRegistered) {
        setIsRegistered(true);
      }
    } catch (error) {
      console.error("등록 상태 확인 오류:", error);
      Sentry.captureException(error, {
        tags: { component: 'CustomWaitlist', operation: 'checkRegistrationStatus' },
        extra: { waitlistId, isSignedIn, userId: user?.id }
      });
      // 오류가 발생해도 사용자에게는 보여주지 않음 (기본 상태 유지)
    } finally {
      setIsCheckingRegistration(false);
    }
  }, [waitlistId, isSignedIn, user]);

  // 컴포넌트 마운트 시 등록 상태 확인 (로그인된 사용자만) — 외부 데이터 fetch, JCG-2034 마이그레이션 예정
  useEffect(() => {
    if (isSignedIn && user) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      checkRegistrationStatus();
    } else {
      setIsCheckingRegistration(false);
    }
  }, [isSignedIn, user, checkRegistrationStatus]);

  // 이메일 인증 코드 발송
  const handleSendVerification = async () => {
    if (!email.trim()) {
      setError("이메일을 입력해주세요.");
      return;
    }

    if (!validateEmail(email)) {
      setError("올바른 이메일 형식을 입력해주세요.");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      // 먼저 해당 이메일로 이미 등록되어 있는지 확인
      const checkResponse = await fetch("/api/waitlist/check-registration", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, waitlistId }),
      });

      const checkResult = await checkResponse.json();

      if (checkResponse.ok && checkResult.isRegistered) {
        setError("이미 대기자 명단에 등록된 이메일입니다.");
        return;
      }

      // 등록되지 않은 경우에만 인증 코드 발송
      const response = await fetch("/api/waitlist/send-verification", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, waitlistId }),
      });

      const result = await response.json();

      if (response.ok) {
        setIsEmailSent(true);
      } else {
        setError(result.error || "인증 코드 발송에 실패했습니다.");
      }
    } catch (error) {
      console.error("인증 코드 발송 오류:", error);
      Sentry.captureException(error, {
        tags: { component: 'CustomWaitlist', operation: 'sendVerification' },
        extra: { email, waitlistId }
      });
      setError("인증 코드 발송에 실패했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  // 인증 코드 확인 및 대기자 등록
  const handleVerifyAndRegister = async () => {
    if (!verificationCode.trim()) {
      setError("인증 코드를 입력해주세요.");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const response = await fetch("/api/waitlist/verify-and-register", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          verificationCode,
          waitlistId,
        }),
      });

      const result = await response.json();

      if (response.ok) {
        setIsRegistered(true);
      } else {
        setError(result.error || "대기자 등록에 실패했습니다.");
      }
    } catch (error) {
      console.error("대기자 등록 오류:", error);
      Sentry.captureException(error, {
        tags: { component: 'CustomWaitlist', operation: 'verifyAndRegister' },
        extra: { email, waitlistId }
      });
      setError("대기자 등록에 실패했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  // 로그인된 사용자 대기자 등록
  const handleLoggedInRegister = async () => {
    if (!user?.emailAddresses?.[0]?.emailAddress) {
      setError("이메일 정보를 가져올 수 없습니다.");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const response = await fetch("/api/waitlist/register", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: user.emailAddresses[0].emailAddress,
          clerkUserId: user.id,
          waitlistId,
        }),
      });

      const result = await response.json();

      if (response.ok) {
        setIsRegistered(true);
      } else {
        setError(result.error || "대기자 등록에 실패했습니다.");
      }
    } catch (error) {
      console.error("대기자 등록 오류:", error);
      Sentry.captureException(error, {
        tags: { component: 'CustomWaitlist', operation: 'loggedInRegister' },
        extra: { waitlistId, userId: user.id, email: user.emailAddresses[0].emailAddress }
      });
      setError("대기자 등록에 실패했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  // 이메일 형식 검증
  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // 등록 상태 확인 중
  if (isCheckingRegistration) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardContent className="pt-6 text-center">
          <div className="text-muted-foreground text-6xl mb-4" aria-hidden="true">⏳</div>
          <h3 className="text-xl font-semibold mb-2">등록 상태 확인 중...</h3>
          <p className="text-muted-foreground">
            잠시만 기다려주세요.
          </p>
        </CardContent>
      </Card>
    );
  }

  // 등록 완료 상태
  if (isRegistered) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardContent className="pt-6 text-center">
          <div className="text-green-600 dark:text-green-400 text-6xl mb-4" aria-hidden="true">✓</div>
          <h3 className="text-xl font-semibold mb-2">대기자 등록 완료!</h3>
          <p className="text-muted-foreground">
            출시 알림을 이메일로 받으실 수 있습니다.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center">
        <CardTitle>대기자 명단 등록</CardTitle>
        <CardDescription>
          {isSignedIn
            ? "로그인된 계정으로 대기자 명단에 등록하세요"
            : "이메일 인증 후 대기자 명단에 등록하세요"}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isSignedIn ? (
          // 로그인된 사용자
          <div className="space-y-4">
            <div className="p-4 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">
                <strong>{user.emailAddresses?.[0]?.emailAddress}</strong>로 등록됩니다.
              </p>
            </div>
            <Button
              onClick={handleLoggedInRegister}
              disabled={isLoading}
              className="w-full"
            >
              {isLoading ? "등록 중..." : "대기자 명단 등록하기"}
            </Button>
          </div>
        ) : (
          // 비로그인 사용자
          <div className="space-y-4">
            {!isEmailSent ? (
              // 이메일 입력 단계
              <form
                className="space-y-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSendVerification();
                }}
              >
                <div>
                  <label htmlFor="waitlist-email" className="sr-only">
                    이메일
                  </label>
                  <Input
                    id="waitlist-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    placeholder="이메일을 입력하세요"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isLoading}
                    aria-describedby={error ? "waitlist-error" : undefined}
                  />
                </div>
                <Button
                  type="submit"
                  disabled={isLoading || !email.trim()}
                  className="w-full"
                >
                  {isLoading ? "발송 중..." : "인증 코드 발송"}
                </Button>
              </form>
            ) : (
              // 인증 코드 입력 단계
              <form
                className="space-y-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleVerifyAndRegister();
                }}
              >
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    <strong>{email}</strong>로 인증 코드를 발송했습니다.
                  </p>
                </div>
                <div>
                  <label htmlFor="waitlist-code" className="sr-only">
                    인증 코드
                  </label>
                  <Input
                    id="waitlist-code"
                    name="verificationCode"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="인증 코드를 입력하세요"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    disabled={isLoading}
                    aria-describedby={error ? "waitlist-error" : undefined}
                  />
                </div>
                <Button
                  type="submit"
                  disabled={isLoading || !verificationCode.trim()}
                  className="w-full"
                >
                  {isLoading ? "등록 중..." : "대기자 명단 등록하기"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsEmailSent(false);
                    setVerificationCode("");
                    setError("");
                  }}
                  className="w-full"
                >
                  이메일 다시 입력
                </Button>
              </form>
            )}
          </div>
        )}

        {error && (
          <div
            id="waitlist-error"
            role="alert"
            aria-live="polite"
            className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg"
          >
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
